import { baseApi } from '../../services/baseApi';
import { ApiEndPoints } from '../../constants/apiEndPoints';

/* ------------------------------------------------------------------ types */

export type PlanMode = 'plan' | 'add_program' | 'remove_program';

export type PlanStatus =
  | 'describing'
  | 'questioning'
  | 'drafting'
  | 'review'
  | 'applied'
  | 'failed'
  | 'cancelled';

/** A file attached to the description (fixed formats, any size). */
export interface Attachment {
  name: string;
  type: string;
  size: number;
  url: string;
  extract?: string;
}

/** add_program: 2–3 combined clarifying questions, answered in plain text. */
export interface ProgramQuestion {
  id: string;
  text: string;
}

export interface SubtaskDraft {
  title: string;
  description?: string;
  due_offset_days?: number | null;
  due_date?: string | null;
  suggested_assignee_id?: number | null;
  assignee_role_hint?: string | null;
  assignee_reason?: string | null;
  /** set by the review screen when the team edits this item by hand */
  _byUser?: boolean;
}

export interface TaskDraft {
  ref?: string | null;
  title: string;
  description?: string;
  priority?: string;
  due_offset_days?: number | null;
  due_date?: string | null;
  suggested_assignee_id?: number | null;
  assignee_role_hint?: string | null;
  assignee_reason?: string | null;
  depends_on?: string[];
  needs_confirmation?: boolean;
  subtasks?: SubtaskDraft[];
  _byUser?: boolean;
}

export interface WorkstreamDraft {
  key: string;
  title: string;
  tasks: TaskDraft[];
}

export interface DraftPlan {
  event_date?: string | null; // the date the plan is built around (team's stated date wins)
  complexity?: string;
  complexity_note?: string;
  assumptions?: string[];
  trimmed?: string[];
  workstreams?: WorkstreamDraft[];
}

/** A proposal against an EXISTING task (by its real id). */
export interface TaskTouch {
  task_id: number;
  reason: string;
  title?: string | null;
  description?: string | null;
  priority?: string | null;
  due_date?: string | null;
  suggested_assignee_id?: number | null;
  assignee_reason?: string | null;
  _byUser?: boolean;
}

export interface ChangeSet {
  summary: string;
  add: WorkstreamDraft[];
  modify: TaskTouch[];
  remove: TaskTouch[];
  /** completed / in-progress tasks no longer needed — never touched, only marked */
  flag: TaskTouch[];
  notes: string[];
}

/** An existing task in the planner's flat shape (GET ai/engagement/{id}/tasks/). */
export interface ExistingTask {
  id: number;
  parent_id: number | null;
  title: string;
  description: string;
  status: string | null;
  priority: string | null;
  due_date: string | null;
  assignee_id: number | null;
  assignee: string | null;
  part: string | null;
  protected: boolean;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  text: string;
  at?: string;
  version?: number;
  error?: boolean;
  changed_refs?: string[];
  removed_titles?: string[];
}

/** A live stage event while the session is 'drafting' (polled). */
export interface ProgressEvent {
  at: string;
  stage: string;
  text: string;
  pct: number;
  done: boolean;
}

export interface Revision {
  version: number;
  label: string;
  at: string;
  doc: DraftPlan | ChangeSet;
}

export interface PlanSession {
  id: number;
  engagement_id: number;
  created_by?: number | null;
  status: PlanStatus;
  mode: PlanMode;
  description?: string | null;
  attachments?: Attachment[];
  questions?: ProgramQuestion[];
  answers?: Record<string, string>;
  draft_plan?: DraftPlan | null;
  change_set?: ChangeSet | null;
  messages?: ChatMessage[];
  revisions?: Revision[];
  progress?: ProgressEvent[];
  applied_task_ids?: number[] | null;
  usage?: Record<string, number>;
  error?: string | null;
  created_at?: string;
  updated_at?: string;
  // present on the apply response
  created_task_ids?: number[];
  modified_task_ids?: number[];
  removed_task_ids?: number[];
  flagged_task_ids?: number[];
  count?: number;
  notes?: string[];
  engagement_date_updated?: boolean;
  // present on the attach response
  attachment?: Attachment;
}

const base = ApiEndPoints.aiPlan; // 'ai/plan'

/* ------------------------------------------------------------------ slice */

export const plannerApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    // Create a session (context pack only). mode: plan | add_program | remove_program
    startPlan: build.mutation<PlanSession, { engagement: number | string; mode?: PlanMode }>({
      query: (body) => ({ url: `${base}/`, method: 'POST', body }),
    }),
    // Also polled (every ~1.5 s) while status is 'drafting' to stream progress events.
    getPlan: build.query<PlanSession, number | string>({
      query: (id) => ({ url: `${base}/${id}/` }),
    }),
    getPlannerTasks: build.query<{ tasks: ExistingTask[] }, number | string>({
      query: (engagementId) => ({ url: `ai/engagement/${engagementId}/tasks/` }),
      providesTags: ['Tasks'],
    }),
    // Attach a file to the description (fixed formats, any size).
    attachFile: build.mutation<PlanSession, { id: number | string; file: File }>({
      query: ({ id, file }) => {
        const form = new FormData();
        form.append('file', file);
        return { url: `${base}/${id}/attach/`, method: 'POST', body: form };
      },
    }),
    detachFile: build.mutation<PlanSession, { id: number | string; name: string }>({
      query: ({ id, name }) => ({ url: `${base}/${id}/attach/`, method: 'DELETE', body: { name } }),
    }),
    // Submit the description. add_program -> 2–3 questions (synchronous). plan / remove_program ->
    // returns 202 with status 'drafting'; the work runs in the background, poll getPlan for progress.
    describe: build.mutation<PlanSession, { id: number | string; description: string }>({
      query: ({ id, description }) => ({ url: `${base}/${id}/describe/`, method: 'POST', body: { description } }),
    }),
    // add_program: answers to the clarifying questions -> 202, change set built in the background.
    submitAnswers: build.mutation<PlanSession, { id: number | string; answers: Record<string, string> }>({
      query: ({ id, answers }) => ({ url: `${base}/${id}/answers/`, method: 'POST', body: { answers } }),
    }),
    // One chat turn. `draft` is the review screen's edited copy (persisted before the turn).
    sendMessage: build.mutation<PlanSession, { id: number | string; text: string; draft?: DraftPlan | ChangeSet }>({
      query: ({ id, text, draft }) => ({ url: `${base}/${id}/message/`, method: 'POST', body: { text, draft } }),
    }),
    revertPlan: build.mutation<PlanSession, { id: number | string; version: number }>({
      query: ({ id, version }) => ({ url: `${base}/${id}/revert/`, method: 'POST', body: { version } }),
    }),
    editDraft: build.mutation<PlanSession, { id: number | string; draft_plan?: DraftPlan; change_set?: ChangeSet }>({
      query: ({ id, ...body }) => ({ url: `${base}/${id}/draft/`, method: 'PATCH', body }),
    }),
    applyPlan: build.mutation<PlanSession, { id: number | string; status?: string; update_engagement_date?: boolean }>({
      query: ({ id, status, update_engagement_date }) => ({
        url: `${base}/${id}/apply/`, method: 'POST',
        body: { ...(status ? { status } : {}), update_engagement_date: update_engagement_date ?? true },
      }),
      // may also change the engagement's date -> refresh the engagement too
      invalidatesTags: ['AiPlan', 'Tasks', 'ActivityLog', 'Engagements'],
    }),
    cancelPlan: build.mutation<PlanSession, number | string>({
      query: (id) => ({ url: `${base}/${id}/cancel/`, method: 'POST', body: {} }),
    }),
  }),
});

export const {
  useStartPlanMutation,
  useGetPlanQuery,
  useGetPlannerTasksQuery,
  useAttachFileMutation,
  useDetachFileMutation,
  useDescribeMutation,
  useSubmitAnswersMutation,
  useSendMessageMutation,
  useRevertPlanMutation,
  useEditDraftMutation,
  useApplyPlanMutation,
  useCancelPlanMutation,
} = plannerApi;

/* ------------------------------------------------------------------ helpers */

export function planError(err: unknown): string {
  const e = err as { data?: { error?: string; detail?: string } } | undefined;
  return e?.data?.error || e?.data?.detail || 'Something went wrong. Please try again.';
}

export const DOC_ACCEPT = '.pdf,.xlsx,.xls,.csv,.docx,.doc,.txt,.jpg,.jpeg,.png';
export const DOC_FORMATS = 'PDF, Excel, CSV, Word, text or images';

export const MODE_LABEL: Record<PlanMode, string> = {
  plan: 'Plan with AI',
  add_program: 'Add a program',
  remove_program: 'Remove a program',
};

export const isProtectedStatus = (s?: string | null) =>
  ['completed', 'in progress', 'inprogress', 'done', 'closed'].includes((s ?? '').trim().toLowerCase());
